import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFormContext } from "react-hook-form";
import {
  hasNoLiveServer,
  regionLabel,
  regionOptions,
  useAgentServerRegions,
  useOrganizationDefaultRegion,
} from "@/hooks/useAgentServerRegions";
import {
  NoLiveServerNotice,
  RegionsLoadFailedNote,
} from "@/components/agent-server-regions/RegionNotices";

const NO_REGION = "__none__";

interface RegionTabProps {
  // The assistant's organization, whose default region "Organization default" follows.
  organizationId?: string;
}

export default function RegionTab({ organizationId }: RegionTabProps) {
  const { control } = useFormContext();
  const { liveRegions, loaded, loadFailed } = useAgentServerRegions();
  const orgDefault = useOrganizationDefaultRegion(organizationId);

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="agent_server_region"
        render={({ field }) => {
          const saved: string = field.value || "";
          const options = regionOptions(liveRegions, saved);
          // With no region of its own, the assistant follows the organization default.
          const inheriting = saved === "";
          // If the default could not be read, the option says nothing more than its name.
          const defaultKnown = inheriting && orgDefault.loaded;

          return (
            <FormItem>
              <FormLabel>Region</FormLabel>
              <Select
                value={saved || NO_REGION}
                onValueChange={(v) => field.onChange(v === NO_REGION ? "" : v)}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Organization default" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={NO_REGION}>Organization default</SelectItem>
                  {options.map((code) => (
                    <SelectItem key={code} value={code}>
                      {regionLabel(code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                The region decides which agent server places this assistant's calls.
                This applies to V2 calls only; V1 calls ignore it. With "Organization
                default", the organization's default region is used, and if none is
                set, the default server.
              </FormDescription>
              {defaultKnown && (
                <p className="text-sm text-muted-foreground">
                  {orgDefault.region
                    ? `Organization default: ${regionLabel(orgDefault.region)}.`
                    : "Organization default: none is set, so the default server is used."}
                </p>
              )}
              {loadFailed && <RegionsLoadFailedNote />}
              {hasNoLiveServer(loaded, liveRegions, saved) && (
                <NoLiveServerNotice
                  region={saved}
                  consequence="V2 calls for this assistant will fail"
                />
              )}
              {defaultKnown &&
                hasNoLiveServer(loaded, liveRegions, orgDefault.region) && (
                  <NoLiveServerNotice
                    region={orgDefault.region}
                    consequence="V2 calls for this assistant will fail"
                  />
                )}
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
